import { Button, flash, formatDate, Modal, roundNumber, SubmitButton } from "@xola/ui-kit";
import { PurchaseItemStatus, requiredString } from "common";
import pThrottle from "p-throttle";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Event, Experience, Guide, PaymentMethod, Purchase, Seller, Timeslot } from "types";
import { array, boolean, number, object, ref, string } from "yup";
import shallow from "zustand/shallow";
import { api } from "../../api/api";
import { calculatePercentage } from "../../helpers/mathHelper";
import { isDecimalNumber } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { getAllowedPaymentMethods, getExcludedPaymentMethods } from "../../models/Payment";
import { getCustomerName, getOriginalPaymentMethod, isPurchaseWithInteracCard } from "../../models/Purchase";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { OverlayLoader } from "../Common/OverlayLoader";
import { XolaFormik } from "../Common/XolaFormik";
import { Loader } from "../Loader";
import {
    getFlexLevelServiceFeePaidByTraveler,
    getPayload,
    PayloadValues,
    RefundPurchasePayloadType,
} from "../PurchaseModification/Refund/helpers";
import { CancelOptions } from "./CancelBookings/CancelOptions";
import {
    CancelBookingFormPurchaseValues,
    CancelBookingsFormValues,
    EventBookingsTable,
    RefundTerm,
} from "./CancelBookings/EventBookingsTable";
import { updateTimeslotCache } from "./EditEventCapacityModal";
import { getBlackoutSchedulePayload } from "./EventSchedulesHelper";
import { EventSummaryTile } from "./EventSummaryTile";
import { useAuthorization } from "@/hooks/useAuthorization";
import { InteracPurchaseDetails, useInteracPurchaseRefundStore } from "@/stores/interacPurchaseRefundStore";

/**
 *  TODO:
 *
 *  1. MIST Bookings
 *  2. Seller cannot be able to refund/cancel for already refunded booking
 *  3. Multiple Payment Methods
 *  4. Handling if Multiple credit cards are used for booking
 *
 *  These will be handled once we get payments and refunds at purchase item level
 *  from API.
 *
 */

const throttle = pThrottle({
    limit: 1,
    interval: 1000,
});

const validationSchema = object().shape({
    reason: string().required("for cancellation is required"),
    notify: boolean(),
    removeAffilates: boolean(),
    removeGuides: boolean(),
    blackoutEvent: boolean(),
    purchases: array().of(
        object().shape({
            amountPaid: number(),
            isSelected: boolean(),
            refundTerm: requiredString(),
            refundMethod: requiredString(),
            refundAmount: number()
                .required()
                .typeError("Invalid Type")
                .min(0, "Should be zero or a positive number with up to two decimal places")
                .test(
                    "format",
                    "Should be zero or a positive number with up to two decimal places",
                    (value) => Number.isInteger(value) || isDecimalNumber(value),
                )
                .max(ref("amountPaid")),
        }),
    ),
});

/**
 *  object that has ids as key and purchase object will be for respective id key
 *  this is for fastly accessing the purchase by id
 */
interface PurchasesMap {
    [key: string]: Purchase;
}

interface CancelEventBookingsModalProps {
    timeslot: Timeslot;
    isOpen: boolean;
    onClose: (shouldCloseRoster: boolean) => void;
}

export const CancelEventBookingsModal = ({ timeslot, isOpen, onClose }: CancelEventBookingsModalProps) => {
    return (
        <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} className="max-w-[1000px]" onClose={onClose}>
            <ModalContent timeslot={timeslot} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    timeslot: Timeslot;
    onClose: (shouldCloseRoster: boolean) => void;
}

const ModalContent = ({ timeslot, onClose }: ModalContentProps) => {
    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", timeslot.event?.id]);
    if (!event || isLoadingEvent) {
        return <Loader />;
    }

    return <CancelEventBookingsForm timeslot={timeslot} event={event} onClose={onClose} />;
};

interface CancelEventBookingFormProps {
    timeslot: Timeslot;
    event: Event;
    onClose: (shouldCloseRoster: boolean) => void;
}

const CancelEventBookingsForm = ({ timeslot, event, onClose }: CancelEventBookingFormProps) => {
    const { data: experience } = useResource<Experience>(["products", timeslot.product]);
    const seller = useSeller();
    const [selectedCount, setSelectedCount] = useState(0);
    const [successCount, setSuccessCount] = useState(0);
    const [showProgress, setShowProgress] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const { isGranted } = useAuthorization();

    const [setInteracRefundModalOpen, setInteracPurchaseDetails] = useInteracPurchaseRefundStore(
        (state) => [state.setOpen, state.setInteracPurchaseDetails],
        shallow,
    );

    const invalidate = useInvalidate();
    const {
        data: purchases,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
        hasNextPage,
    } = useInfiniteCollection<Purchase>(
        "purchases",
        { "items[in]": event?.purchaseItems.map((item) => item.id).join(",") },
        { keepPreviousData: true },
    );

    if (hasNextPage) {
        fetchNextPage();
    }

    const { data: guides, isLoading: isLoadingGuides } = useCollection<Guide>(["sellers", seller.id, "guides"], {
        events: event?.id,
    });

    /**
     *  Purchases may contain multiple purchaseItems and the order might not
     *  be same for each of them, so filtering out only the purchase item with
     *  current event.
     */
    const purchasesMap: PurchasesMap = {};
    for (const purchase of purchases) {
        const purchaseItem = purchase.items.find(
            (item) => item.event.id === event.id && item.status === PurchaseItemStatus.CONFIRMED,
        );
        if (purchaseItem) {
            purchasesMap[purchase.id] = {
                ...purchase,
                items: purchase.items.filter((item) => item.event.id === event.id),
            };
        }
    }

    const bookingsCount = Object.keys(purchasesMap).length;
    const initialValues = getInitialValues(seller, purchasesMap);

    const throttledCancelAndRefund = throttle(
        async (purchase: Purchase, removeAffilates: boolean, refundPayload: any) => {
            try {
                const item = purchase.items[0];
                await api.patch(`/purchases/${purchase.id}`, {
                    items: [
                        {
                            id: item.id,
                            status: PurchaseItemStatus.CANCELED,
                            partner: removeAffilates ? null : undefined,
                        },
                    ],
                    ...refundPayload,
                });

                return true;
            } catch {
                flash.danger("Error saving new time.");
                return false;
            }
        },
    );

    const { mutateAsync: unAssignGuides } = useMutation(async ({ event, guide }: { event: Event; guide: Guide }) => {
        await api.delete(`/events/${event.id}/guides/${guide.id}`);
    });

    const { mutateAsync: blackoutEvent } = useMutation(async () => {
        const payload = getBlackoutSchedulePayload({ timeslot, event, experience });
        return api.post(`/experiences/${experience!.id}/schedules`, payload);
    });

    const handleSubmit = async (values: CancelBookingsFormValues) => {
        setShowProgress(true);
        const selectedPurchases = [...values.purchases].filter(
            (purchase: CancelBookingFormPurchaseValues) => purchase.isSelected,
        );
        const skippedInteracPurchaseDetails: InteracPurchaseDetails[] = [];

        try {
            for (const selectedPurchase of selectedPurchases) {
                const purchase = purchasesMap[selectedPurchase.id];
                const item = purchase.items.find((purchaseItem) => purchaseItem.product.id === event.experience.id);
                const initialPaymentMethod = getOriginalPaymentMethod(seller, purchase);
                const excludePaymentMethods = getExcludedPaymentMethods(purchase, item);
                const paymentMethods = getAllowedPaymentMethods({
                    seller,
                    isRefund: true,
                    isInvoiceAllowed: false,
                    excludePaymentMethods,
                });

                const payloadValues: PayloadValues = {
                    amount: selectedPurchase.refundAmount,
                    comment: values.reason ?? "Refund payment",
                    notify: values.notify,
                };

                const isFullRefund = selectedPurchase.refundTerm === RefundTerm.FULL;
                const isStoreCredit = selectedPurchase.refundMethod === PaymentMethod.STORE_CREDIT;
                const serviceFeeLineItem = getFlexLevelServiceFeePaidByTraveler(purchase);
                const serviceFee =
                    isFullRefund && serviceFeeLineItem && !isStoreCredit ? serviceFeeLineItem?.amount : 0;

                let isInteracCard = false;
                const refundMethod =
                    paymentMethods.find((paymentMethod) => paymentMethod.id === selectedPurchase.refundMethod) ??
                    initialPaymentMethod;
                if (refundMethod.id === PaymentMethod.CREDIT_CARD) {
                    if (initialPaymentMethod.card) {
                        refundMethod.card = initialPaymentMethod.card;
                    }

                    isInteracCard = isPurchaseWithInteracCard(purchase);
                }

                const refundPayload =
                    selectedPurchase.refundAmount > 0
                        ? getPayload(refundMethod, purchase, item ?? null, payloadValues, true, serviceFee)
                        : {};

                if (isInteracCard && "ops" in refundPayload) {
                    (refundPayload as RefundPurchasePayloadType).ops.pop(); // Remove the refund line item from ops, as we need to handle it separately
                    skippedInteracPurchaseDetails.push({
                        purchaseId: purchase.id,
                        itemId: item?.id,
                    });
                }

                await throttledCancelAndRefund(purchase, values.removeAffiliates, refundPayload);
                setSuccessCount((successCount) => successCount + 1);
            }

            if (values.removeGuides) {
                const assignedGuides = event?.guides.map((item) => item.guide.id) ?? [];
                const filteredGuides = guides.filter((guide) => assignedGuides.includes(guide.id));
                for (const guide of filteredGuides) {
                    await unAssignGuides({ event, guide });
                }
            }

            if (values.blackoutEvent) {
                await blackoutEvent();
            }
        } catch (error) {
            console.log("Error in Cancelling Purchases", error);
        }

        invalidateAndClose(skippedInteracPurchaseDetails);
    };

    const invalidateAndClose = (skippedInteracPurchaseDetails?: InteracPurchaseDetails[]) => {
        invalidate("purchases", "collection");
        invalidate("purchaseItems", "collection");
        invalidate("events", timeslot.event.id);
        setTimeout(() => {
            const timeslotParams = {
                timeslot,
                event,
                productId: null,
                startDate: null,
            };

            const updateParams = {
                isGranted,
            };

            if (skippedInteracPurchaseDetails && skippedInteracPurchaseDetails?.length > 0) {
                setInteracPurchaseDetails(skippedInteracPurchaseDetails);
                setInteracRefundModalOpen();
            }

            onClose(true);
            updateTimeslotCache(timeslotParams, updateParams);
        }, 1000);
    };

    const abortThrottle = () => {
        throttledCancelAndRefund.abort();
        invalidateAndClose();
    };

    const isFetching = isLoading || isLoadingGuides || isFetchingNextPage;
    const showCancelButton = () => selectedCount > 1 && selectedCount - 1 > successCount;

    if (isLoading) {
        return <Loader />;
    }

    if (showProgress) {
        return (
            <OverlayLoader
                isOpen
                color="primary"
                description={
                    <>
                        <var data-var="successCount">
                            {successCount} / {selectedCount}
                        </var>
                        &nbsp;reservations processed
                    </>
                }
                showCancelButton={showCancelButton()}
                progress={calculatePercentage(successCount, selectedCount)}
                onCancel={() => {
                    setShowCancelModal(true);
                    setShowProgress(false);
                }}
            />
        );
    }

    return showCancelModal ? (
        <>
            <Modal.Header>Cancel Operation?</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col text-center text-gray-darker">
                    <span>
                        Cancelling this operation may result in some of your reservations being changed while others
                        remaining unchanged.
                    </span>
                    <span className="mt-3">Do you still want to cancel this operation?</span>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline"
                    color="secondary"
                    onClick={() => {
                        setShowCancelModal(false);
                        setShowProgress(true);
                    }}
                >
                    Back
                </Button>
                <Button color="danger" onClick={abortThrottle}>
                    Cancel
                </Button>
            </Modal.Footer>
        </>
    ) : (
        <XolaFormik
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setValues, setFieldValue, values, errors }) => {
                const handleTableChange = (value: CancelBookingFormPurchaseValues[]) => {
                    const updatedValues = { ...values };
                    const selectedItemsCount = value.filter((item: any) => item.isSelected).length;
                    updatedValues.purchases = value;
                    if (selectedItemsCount !== value.length) {
                        updatedValues.removeGuides = false;
                    }

                    setValues(updatedValues);
                    setSelectedCount(selectedItemsCount);
                };

                const handlePurchaseFieldChange = (index: number, field: string, value: any) => {
                    setFieldValue(`purchases.${index}.${field}`, value);
                    if (field === "isSelected") {
                        setSelectedCount(value ? selectedCount + 1 : selectedCount - 1);
                    }
                };

                const handleRowChange = (index: number, value: any) => {
                    setFieldValue(`purchases.${index}`, value);
                };

                return (
                    <fieldset>
                        <Modal.Header
                            description={
                                <>
                                    These are reservations for <var data-var="experienceName">{experience?.name}</var>
                                    &nbsp;on&nbsp;
                                    <var data-var="arrivalDatetime">{formatDate(timeslot.arrivalDatetime, "LLL")}</var>.
                                    Only travelers with valid emails IDs will be notified and refund will be issued to
                                    the original payment method
                                </>
                            }
                            data-testid="cancel-event-bookings-modal-header"
                        >
                            Cancel Multiple Reservations
                        </Modal.Header>
                        <Modal.Body>
                            <div className="space-y-4">
                                <EventSummaryTile event={event} />
                                {bookingsCount > 0 ? (
                                    <>
                                        <EventBookingsTable
                                            isLoading={isFetching}
                                            purchasesMap={purchasesMap}
                                            values={values}
                                            errors={errors.purchases as any}
                                            onChange={handleTableChange}
                                            onFieldChange={handlePurchaseFieldChange}
                                            onRowChange={handleRowChange}
                                        />
                                        <CancelOptions
                                            bookingsCount={bookingsCount}
                                            selectedCount={selectedCount}
                                            timeslot={timeslot}
                                            values={values}
                                            onChange={setFieldValue}
                                        />
                                    </>
                                ) : (
                                    <p className="py-10 text-center">No Bookings found in the event</p>
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="flex justify-end">
                            {bookingsCount > 0 ? (
                                <NotifyCustomerCheckbox
                                    isChecked={values.notify}
                                    onChange={(e: any) => {
                                        setFieldValue("notify", e.target.checked);
                                    }}
                                />
                            ) : null}
                            <Button
                                variant="outline"
                                color="secondary"
                                data-testid="event-bookings-table-cancel-button"
                                onClick={() => onClose(false)}
                            >
                                Cancel
                            </Button>
                            <SubmitButton
                                type="submit"
                                disabled={isFetching || selectedCount === 0}
                                color="danger"
                                data-testid="event-bookings-table-cancel-bookings-button"
                                onClick={handleSubmit}
                            >
                                Cancel Reservations
                            </SubmitButton>
                        </Modal.Footer>
                    </fieldset>
                );
            }}
        </XolaFormik>
    );
};

const getInitialValues = (seller: Seller, purchasesMap: PurchasesMap) => {
    return {
        reason: "",
        notify: true,
        removeAffiliates: true,
        removeGuides: false,
        blackoutEvent: false,
        purchases: Object.values(purchasesMap).map((purchase: Purchase) => {
            const purchaseItemAmount = roundNumber(
                purchase.currency,
                purchasesMap[purchase.id].amount - purchasesMap[purchase.id].balance,
            );
            const originalMethod = getOriginalPaymentMethod(seller, purchase);

            return {
                id: purchase.id,
                customerName: getCustomerName(purchase),
                amountPaid: purchaseItemAmount,
                isSelected: false,
                refundTerm: RefundTerm.FULL,
                refundAmount: purchaseItemAmount,
                refundMethod: originalMethod.id,
            };
        }),
    };
};
